/**
 * The chart view cycle: what the toolbar button steps through, and who is allowed to see what.
 *
 * Three views, but not everyone gets three. `hidden` draws the price series invisibly so the
 * study overlays — planetary bands, trend lines, cycle markers — can be read against a bare
 * canvas. That is an analysis affordance, and it is admin-only.
 *
 * ## The gate is here, not in the toolbar
 *
 * `nextChartView` takes `isAdmin` and enforces it, rather than trusting the button to be hidden.
 * That matches how the admin pivot studies work in App.js: the toolbar control is a convenience,
 * never the guard. It also settles a case the toolbar cannot — a session whose entitlement drops
 * while `hidden` is the active view (a token refresh, or signing back in as a standard user).
 * Without normalisation that user is left staring at an invisible chart, cycling between two
 * states that never restore it. {@link normalizeChartView} is what makes that unreachable, and
 * callers should render through it rather than through the raw state.
 *
 * ## What the icon means
 *
 * The button shows **the view you will get if you click it**, not the one you are in. That is
 * already how the two-state toggle behaved — in line view it showed the candlestick icon — so the
 * third state extends the existing rule rather than introducing a second one.
 */

/** The three views. `hidden` is admin-only. */
export const CHART_VIEW = {
  LINE: 'line',
  CANDLESTICK: 'candlestick',
  HIDDEN: 'hidden',
};

/**
 * THE TOOLBAR CYCLE IS NOW TWO STATES FOR EVERYONE.
 *
 * <p>`hidden` moved to the Admin Panel, where it is a SWITCH rather than a stop on a cycle — and
 * a switch is the better shape for it: a reader who wants the bare canvas wants to turn it on and
 * off, not step through candlesticks to get back. It also takes the admin-only state off a
 * control every customer presses, which is the point of the relocation.
 *
 * <p>`isAdmin` is still a parameter and the reachable set still depends on it, because
 * {@link normalizeChartView} is what rescues a session whose entitlement drops while `hidden` is
 * active — without it that user stares at an invisible chart with no way back. The TOOLBAR cycle
 * no longer offers it; the set of views an admin may HOLD is unchanged.
 */
const STANDARD_VIEWS = [CHART_VIEW.LINE, CHART_VIEW.CANDLESTICK];
const ADMIN_VIEWS = [CHART_VIEW.LINE, CHART_VIEW.CANDLESTICK, CHART_VIEW.HIDDEN];

/** The views this entitlement may HOLD. Used for normalisation, not for the toolbar button. */
export function cycleFor(isAdmin) {
  return isAdmin ? ADMIN_VIEWS : STANDARD_VIEWS;
}

/**
 * The views the TOOLBAR button steps through — two, always.
 *
 * <p>Separate from {@link cycleFor} on purpose. Folding them back together is how `hidden` would
 * creep back onto the toolbar the next time somebody reads one function and assumes it answers
 * both questions.
 */
export function toolbarCycle() {
  return STANDARD_VIEWS;
}

/**
 * The view to actually render, given what is stored and who is asking.
 *
 * Anything unreachable for this user — `hidden` without admin, or a value that is not a view at
 * all — resolves to the line chart. Falling back to a *visible* view is the point: the failure
 * this prevents is a blank chart with no way back.
 */
export function normalizeChartView(view, isAdmin) {
  return cycleFor(isAdmin).includes(view) ? view : CHART_VIEW.LINE;
}

/** The next view in the cycle. Normalises first, so an out-of-reach current view cannot stick. */
export function nextChartView(view, isAdmin) {
  const cycle = toolbarCycle();
  // A view the cycle does not contain -- `hidden`, set from the Admin Panel -- steps to the FIRST
  // entry rather than nowhere. indexOf returns -1 for it, and (-1 + 1) % 2 is 0, which is the
  // line chart: pressing the toolbar button while hidden restores a visible chart, which is
  // exactly what someone pressing it in that state wants.
  const index = cycle.indexOf(normalizeChartView(view, isAdmin));
  return cycle[(index + 1) % cycle.length];
}

/** Turns the invisible view on or off directly, for the Admin Panel's switch. */
export function withPriceHidden(view, hidden, isAdmin) {
  if (hidden) return isAdmin ? CHART_VIEW.HIDDEN : normalizeChartView(view, isAdmin);
  return view === CHART_VIEW.HIDDEN ? CHART_VIEW.CANDLESTICK : view;
}

/** True when the price series should be drawn invisibly. */
export function isChartHidden(view) {
  return view === CHART_VIEW.HIDDEN;
}

/** Tooltip copy naming the view a click leads to, so the button explains itself. */
export function chartViewActionLabel(view, isAdmin) {
  switch (nextChartView(view, isAdmin)) {
    case CHART_VIEW.CANDLESTICK:
      return 'Switch the chart to candlesticks';
    case CHART_VIEW.HIDDEN:
      return 'Hide the price series, keeping studies and drawings visible (admin)';
    default:
      return 'Switch the chart to a line';
  }
}
